import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  Suspense,
} from "react";
import GallerySkeleton from "./GallerySkeleton";
import { hasGalleryBooted } from "../../pages/Gallery/galleryReturnCache";

// Login-loader remount fix (Gordon, main.a032679f.js).
//
// The gallery boot loader used to appear TWICE, back-to-back, at the same
// /gallery/:id route: once as the route-level Suspense fallback while the lazy
// Gallery chunk downloaded (instance A), then again as Gallery's own
// !initialLoadComplete <GallerySkeleton/> while its data loaded (instance B). A
// and B are two distinct React instances at two tree positions, so the
// chunk->data handoff tore down DOM node A and built DOM node B — the visible
// present->absent->present blink Gordon reported.
//
// GalleryBootBoundary makes the boot loader a SINGLE, stable instance that stays
// mounted across BOTH phases. Gallery (when hoisted) renders null while loading
// and calls markReady() once its data has loaded (or failed); that reveal is the
// only thing that unmounts the one skeleton. No teardown/rebuild in between.

// Default = standalone (no boot owner): hoisted=false + no-op markReady, so a
// <Gallery/> rendered OUTSIDE a boundary behaves exactly as before this change.
export const GalleryBootContext = createContext({
  hoisted: false,
  markReady: () => {},
});

export const useGalleryBoot = () => useContext(GalleryBootContext);

// Pure decision for Gallery's initial-load branch — unit-testable with no mount:
//   loaded               -> undefined (caller renders real content)
//   loading + hoisted    -> null (the boundary owns the ONE skeleton)
//   loading + standalone -> <GallerySkeleton/> (unchanged legacy behaviour)
export function galleryInitialLoadSkeleton({ initialLoadComplete, hoisted }) {
  if (initialLoadComplete) return undefined;
  return hoisted ? null : <GallerySkeleton />;
}

// T22.656: `bootKey` (the gallery's user id) lets a RETURN to a gallery this
// tab already booted (Gallery -> View Wheel -> back) start `ready`, so the
// "Loading your Wheels" hero loader does not replay over a page that can paint
// from cache. While the boundary is ready it no longer owns the skeleton
// (hoisted=false), so if Gallery still has to wait for data it falls back to
// its own standalone <GallerySkeleton/> instead of rendering nothing.
export function GalleryBootBoundary({ children, bootKey }) {
  const [ready, setReady] = useState(() => hasGalleryBooted(bootKey));
  const value = useMemo(
    () => ({ hoisted: !ready, markReady: () => setReady(true) }),
    [ready],
  );
  // Order matters (recon-0925-P2-qa-open-rows-part02,
  // stg-0922-gallery-header-renders-1000px-below-the-viewport-at-1920-only):
  // `children` (Gallery, when hoisted) still renders its real `earlyHeader`
  // unconditionally while loading -- only its OWN inner skeleton slot goes
  // null. Mounting `children` before the boundary's GallerySkeleton keeps
  // that early header at the top of the DOM, matching the already-correct
  // standalone (non-hoisted) order in pages/Gallery/index.jsx
  // (`{earlyHeader}{skeleton}`). The previous skeleton-first order stacked
  // the real header a full `min-height: 100vh` skeleton block below the
  // fold at every width, not just 1920 -- confirmed live at 1440x900,
  // 1800x1080 and 1920x1000.
  return (
    <GalleryBootContext.Provider value={value}>
      <Suspense fallback={null}>{children}</Suspense>
      {!ready && <GallerySkeleton />}
    </GalleryBootContext.Provider>
  );
}

export default GalleryBootBoundary;
